import { h } from '../lib/dom.js';
import { getState, fmtNum } from '../store.js';
import { iconMarkup } from '../icons/index.js';
import { t } from '../i18n/index.js';
import {
  cancelMigrateTreasury, setMigrateTreasuryAddress, setMigrateTreasuryMainnetAck, onMigrateTreasuryContinue, onMigrateTreasuryBack,
  onMigrateTreasuryConfirm, closeMigrateTreasury, migrateTreasuryChainsNeeded, migrateTreasuryReviewReady, gasCheckFor
} from '../actions.js';

// Same visually-hidden-native-checkbox + lucide square/square-check icon
// pattern as send.js/transfer.js's own local copies — see send.js's
// customCheckbox comment for why this is duplicated locally rather than
// shared/exported (a 4-line pattern, not worth a cross-cutting refactor).
function customCheckbox(checked, onchange) {
  return h('span', { class: 'custom-checkbox-wrap' }, [
    h('input', { type: 'checkbox', class: 'custom-checkbox-input', checked, onchange }),
    h('span', { class: 'custom-checkbox-icon', 'aria-hidden': 'true', html: iconMarkup(checked ? 'squareCheck' : 'square', { size: 20, color: checked ? '#4D4DFF' : '#757993' }) })
  ]);
}

function renderForm(s) {
  const mt = s.migrateTreasury;
  const chainsNeeded = migrateTreasuryChainsNeeded(s);
  if (chainsNeeded.length === 0) {
    return [
      h('div', { class: 'text-sm text-muted' }, t('modals.migrateTreasury.noBalances')),
      h('div', { class: 'btn-row' }, [
        h('button', { class: 'btn btn-secondary', onclick: cancelMigrateTreasury }, t('common.cancel'))
      ])
    ];
  }
  return [
    h('div', { class: 'text-sm text-muted' }, t('modals.migrateTreasury.intro')),
    ...chainsNeeded.map((chain) => h('div', {}, [
      h('div', { class: 'card-label' }, t('modals.migrateTreasury.destinationLabel', { chain })),
      h('input', {
        placeholder: chain, value: mt.toAddress[chain] || '',
        oninput: (e) => setMigrateTreasuryAddress(chain, e.target.value)
      }),
      h('div', { class: 'field-error' }, mt.errors[chain] || '')
    ])),
    h('div', { class: 'btn-row' }, [
      h('button', { class: 'btn btn-secondary', onclick: cancelMigrateTreasury }, t('common.cancel')),
      h('button', { class: 'btn btn-primary', onclick: onMigrateTreasuryContinue }, t('modals.migrateTreasury.continueReview'))
    ])
  ];
}

function legStatusLabel(leg, gasOk) {
  if (leg.status === 'sending') return t('modals.migrateTreasury.statusSending');
  if (leg.status === 'done') return t('modals.migrateTreasury.statusDone');
  if (leg.status === 'failed') return leg.errorMsg ? `${t('modals.migrateTreasury.statusFailed')} — ${leg.errorMsg}` : t('modals.migrateTreasury.statusFailed');
  if (leg.status === 'skipped') return t('modals.migrateTreasury.statusSkipped');
  if (leg.feeLoading) return t('modals.migrateTreasury.feeLoading');
  if (leg.feeError) return t('modals.migrateTreasury.feeUnavailable');
  if (gasOk === null) return t('modals.migrateTreasury.gasChecking');
  if (gasOk === false) return t('modals.migrateTreasury.gasInsufficient');
  return t('modals.migrateTreasury.statusPending');
}

function renderLeg(leg, legs) {
  const gasOk = leg.feeLoading || leg.feeError ? null : gasCheckFor(legs, leg);
  const statusIcon = leg.status === 'done' ? 'circleCheckBig'
    : leg.status === 'failed' ? 'x'
      : (leg.feeError || leg.status === 'skipped' || gasOk === false) ? 'triangleAlert'
        : null;
  const statusColor = leg.status === 'done' ? '#1a8a4c' : (leg.status === 'failed' ? '#c0392b' : '#e08a00');
  return h('div', { class: 'card stack' }, [
    h('div', { class: 'review-row' }, [
      h('span', { class: 'k' }, t('modals.migrateTreasury.legAmount', { amount: fmtNum(Number(leg.amount)), ccy: leg.ccy, account: leg.accountName })),
      h('span', { class: 'v' }, leg.chain)
    ]),
    h('div', { class: 'mono review-address' }, leg.toAddress),
    h('div', { class: 'review-row' }, [
      h('span', { class: 'k' }, statusIcon ? h('span', { class: 'flex-gap-sm items-center' }, [
        h('span', { html: iconMarkup(statusIcon, { size: 14, color: statusColor }) }),
        legStatusLabel(leg, gasOk)
      ]) : legStatusLabel(leg, gasOk)),
      h('span', { class: 'v' }, leg.fee != null ? leg.fee : '')
    ])
  ]);
}

function renderReview(s) {
  const mt = s.migrateTreasury;
  const ready = migrateTreasuryReviewReady(mt);
  const executableCount = mt.legs.filter((l) => !l.feeLoading && !l.feeError && l.fee != null && gasCheckFor(mt.legs, l) === true).length;
  const anyExecuted = mt.legs.some((l) => l.status !== 'pending');
  const mainnetGuard = !s.testnetEnabled
    ? h('div', { class: 'mainnet-guard' }, [
        h('label', { class: 'checkbox-row mainnet-guard-row' }, [
          customCheckbox(mt.mainnetAck, (e) => setMigrateTreasuryMainnetAck(e.target.checked)),
          t('modals.migrateTreasury.mainnetAckLabel')
        ])
      ])
    : null;
  const doneCount = mt.legs.filter((l) => l.status === 'done').length;
  const finished = !mt.sending && anyExecuted;
  const confirmBlocked = mt.sending || !ready || executableCount === 0 || (!s.testnetEnabled && !mt.mainnetAck) || anyExecuted;
  return [
    h('div', { class: 'text-sm text-muted' }, t('modals.migrateTreasury.reviewIntro', { count: executableCount })),
    ...mt.legs.map((leg) => renderLeg(leg, mt.legs)),
    mainnetGuard,
    finished ? h('div', { class: 'text-sm' }, t('modals.migrateTreasury.summary', { done: doneCount, total: mt.legs.length })) : null,
    h('div', { class: 'btn-row' }, [
      !finished ? h('button', { class: 'btn btn-secondary', disabled: mt.sending, onclick: onMigrateTreasuryBack }, t('common.back')) : null,
      !finished
        ? h('button', {
            class: 'btn btn-primary btn-emphasis', disabled: confirmBlocked, onclick: confirmBlocked ? null : onMigrateTreasuryConfirm
          }, mt.sending ? t('modals.migrateTreasury.sweeping') : t('modals.migrateTreasury.confirmSweep'))
        : h('button', { class: 'btn btn-primary', onclick: closeMigrateTreasury }, t('common.done'))
    ])
  ];
}

export function renderMigrateTreasuryModal() {
  const s = getState();
  if (!s.showMigrateTreasury) return null;
  const mt = s.migrateTreasury;
  return h('div', { class: 'modal-scrim modal-scrim-panel' }, [
    h('div', { class: 'modal-card stack' }, [
      h('div', { class: 'modal-title' }, mt.step === 'review' ? t('modals.migrateTreasury.reviewTitle') : t('modals.migrateTreasury.title')),
      ...(mt.step === 'review' ? renderReview(s) : renderForm(s))
    ])
  ]);
}
